import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { AlertCircle, ArrowRight, CheckCircle2, Info, Mail, Clock } from "lucide-react";
import { Button } from "@/components/Button";
import { Field } from "@/components/Field";
import { Input } from "@/components/Input";
import { PasswordInput } from "@/components/PasswordInput";
import { AuthLayout } from "@/templates/AuthLayout";
import { cn } from "@/lib/cn";

interface FlashMessages {
  alert?: string | null;
  notice?: string | null;
  info?: string | null;
  success?: string | null;
  error?: string | null;
}

interface Props {
  csrfToken: string;
  flash?: FlashMessages;
  expired?: boolean;
  /** Email to pre-fill (e.g. after a redirect from another flow). */
  email?: string | null;
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export default function Login({ csrfToken, flash, expired, email: initialEmail }: Props) {
  const [email, setEmail] = useState(initialEmail ?? "");
  const [password, setPassword] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittedOnce = useRef(false);

  const emailError = useMemo(() => {
    if (!email) return "Email is required";
    if (!isValidEmail(email)) return "Enter a valid email address";
    return null;
  }, [email]);

  const passwordError = useMemo(() => {
    if (!password) return "Password is required";
    return null;
  }, [password]);

  const showEmailError = (emailTouched || submittedOnce.current) && emailError;
  const showPasswordError = (passwordTouched || submittedOnce.current) && passwordError;

  useEffect(() => {
    document.title = "Sign in · stabiliti";
  }, []);

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    submittedOnce.current = true;
    if (emailError || passwordError) {
      e.preventDefault();
      setEmailTouched(true);
      setPasswordTouched(true);
      return;
    }
    // Real form POST to OmniAuth — disable the button so the user can't
    // double-submit, but let the browser navigate as usual.
    setSubmitting(true);
  }

  const banner = flashBanner(flash);

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Enter your email and password to log into the supplier portal"
      footer={
        <>
          Don't have an account?{" "}
          <a href="/join" className="font-medium text-accent-text hover:underline">
            Get in touch
          </a>
        </>
      }
    >
      <form
        action="/auth/identity/callback"
        method="post"
        noValidate
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
      >
        <input type="hidden" name="authenticity_token" value={csrfToken} />

        {expired && (
          <FlashBanner tone="info" title="Your session has expired" icon={Clock}>
            Please sign in again to continue.
          </FlashBanner>
        )}
        {banner && (
          <FlashBanner tone={banner.tone} title={banner.title}>
            {banner.body}
          </FlashBanner>
        )}

        <Field.Root error={!!showEmailError}>
          <Field.Label>Email</Field.Label>
          <Input
            type="email"
            name="auth_key"
            autoComplete="email"
            inputMode="email"
            placeholder="you@company.com"
            leadingIcon={<Mail />}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            aria-invalid={!!showEmailError || undefined}
            className="h-10"
            autoFocus
          />
          {showEmailError && <Field.Error>{emailError}</Field.Error>}
        </Field.Root>

        <Field.Root error={!!showPasswordError}>
          <Field.Label>Password</Field.Label>
          <PasswordInput
            name="password"
            autoComplete="current-password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setPasswordTouched(true)}
          />
          {showPasswordError && <Field.Error>{passwordError}</Field.Error>}
        </Field.Root>

        <Button type="submit" intent="primary" size="lg" disabled={submitting} className="h-11 w-full">
          {submitting ? "Signing in…" : "Sign in"}
          {!submitting && <ArrowRight />}
        </Button>
      </form>
    </AuthLayout>
  );
}

// Skip the PortalShell — auth pages render bare (AuthLayout supplies its own
// dark brand shell, header, centred body and footer).
Login.layout = (page: ReactNode) => page;

interface BannerSpec {
  tone: "danger" | "info" | "success";
  title: string;
  body?: string;
}

function flashBanner(flash?: FlashMessages): BannerSpec | null {
  if (!flash) return null;
  if (flash.alert) return { tone: "danger", title: "Couldn't sign you in", body: flash.alert };
  if (flash.error) return { tone: "danger", title: "Something went wrong", body: flash.error };
  if (flash.success) return { tone: "success", title: flash.success };
  if (flash.notice) return { tone: "info", title: flash.notice };
  if (flash.info) return { tone: "info", title: flash.info };
  return null;
}

const TONE_BG: Record<BannerSpec["tone"], string> = {
  danger: "bg-danger-bg text-danger-fg",
  success: "bg-success-bg text-success-fg",
  info: "bg-info-bg text-info-fg",
};
const TONE_ICON: Record<BannerSpec["tone"], typeof AlertCircle> = {
  danger: AlertCircle,
  success: CheckCircle2,
  info: Info,
};

function FlashBanner({
  tone,
  title,
  icon,
  children,
}: {
  tone: BannerSpec["tone"];
  title: string;
  icon?: typeof AlertCircle;
  children?: ReactNode;
}) {
  const Icon = icon ?? TONE_ICON[tone];
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={cn(
        "flex items-start gap-2.5 rounded-[var(--radius-md)] px-3 py-2.5 text-sm",
        TONE_BG[tone],
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 text-left">
        <p className="font-medium leading-tight">{title}</p>
        {children && <p className="mt-0.5 text-[13px] opacity-90">{children}</p>}
      </div>
    </div>
  );
}
